import { useEffect, useState, useMemo } from "react";
import { Smartphone, FileUp, X, Users, ShieldCheck, UserCheck, Info } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

/**
 * Import Contacts modal — shared review UI for two entry paths:
 *   1. Android Contact Picker (navigator.contacts.select)
 *   2. .vcf / .csv file upload (all other browsers, including iPhone Safari)
 *
 * Nothing is persisted until the user presses "Import selected contacts".
 * Server does the final dedup + AMDS Member link on POST /contacts/import/commit.
 */

const ANDROID_PICKER_SUPPORTED = () =>
  typeof navigator !== "undefined"
  && "contacts" in navigator
  && navigator.contacts
  && typeof navigator.contacts.select === "function";

// Rough UA sniff for the iPhone helper copy. Never used for security gating —
// only for showing the right hint text.
const IS_IPHONE = () =>
  typeof navigator !== "undefined"
  && /iPad|iPhone|iPod/.test(navigator.userAgent || "");


export default function ImportContactsModal({ open, onClose, onImported }) {
  const [stage, setStage] = useState("chooser");  // chooser | review
  const [proposed, setProposed] = useState([]);   // enriched rows for the review screen
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) { setStage("chooser"); setProposed([]); }
  }, [open]);

  // ---------- Android picker ----------
  const pickFromPhone = async () => {
    if (!ANDROID_PICKER_SUPPORTED()) {
      toast.error("Your browser can't open the phone's contacts directly. Please use the file option below.");
      return;
    }
    try {
      const picked = await navigator.contacts.select(
        ["name", "email", "tel"], { multiple: true },
      );
      if (!picked || picked.length === 0) {
        toast("No contacts selected.");
        return;
      }
      // Reshape into our proposed-contact model and enrich against server.
      const rough = picked.map((c) => ({
        name: (c.name && c.name[0]) || "",
        emails: (c.email || []).map((e) => String(e).toLowerCase()).filter(Boolean),
        phones: (c.tel || []).map((t) => String(t).replace(/[^\d+]/g, "")).filter(Boolean),
      })).filter((c) => c.name || c.emails.length || c.phones.length);
      await enrichAndReview(rough);
    } catch (e) {
      // User cancelled the picker or permission denied — quiet exit.
      if (e && e.name === "SecurityError") {
        toast.error("Permission denied for contacts.");
      }
    }
  };

  // Enrich local picker output with server dedup/member data by uploading
  // a minimal synthetic vCard. Keeps the review flow identical to the file
  // path — one code path server-side, one review UI client-side.
  const enrichAndReview = async (rough) => {
    setBusy(true);
    try {
      // Serialise picker output as a tiny vCard so the parse endpoint can
      // return the same enriched shape.
      const vcf = rough.map((c) => {
        const lines = ["BEGIN:VCARD", "VERSION:3.0",
          `FN:${(c.name || "").replace(/[\n\r]/g, " ")}`];
        c.emails.forEach((e) => lines.push(`EMAIL:${e}`));
        c.phones.forEach((t) => lines.push(`TEL:${t}`));
        lines.push("END:VCARD");
        return lines.join("\n");
      }).join("\n");
      const fd = new FormData();
      fd.append("file", new Blob([vcf], { type: "text/vcard" }), "phone.vcf");
      const { data } = await api.post("/contacts/import/parse", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setProposed(data.contacts.map(toReviewRow));
      setStage("review");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  // ---------- File upload ----------
  const onFileChosen = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";  // allow re-choosing same file later
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) {
      toast.error("That file is over 2 MB — please export a smaller batch.");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/contacts/import/parse", fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setProposed(data.contacts.map(toReviewRow));
      setStage("review");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  // ---------- Review helpers ----------
  const toggleInclude = (i) => setProposed((p) => p.map(
    (r, idx) => idx === i ? { ...r, include: !r.include } : r));
  const updateRow = (i, patch) => setProposed((p) => p.map(
    (r, idx) => idx === i ? { ...r, ...patch } : r));

  const includedCount = useMemo(
    () => proposed.filter((r) => r.include).length, [proposed]);

  const commit = async () => {
    const rows = proposed.filter((r) => r.include).map((r) => ({
      name: r.name,
      email: r.email,
      phone: r.phone || null,
      instrument: r.instrument || null,
      is_favourite: !!r.is_favourite,
      private_notes: r.private_notes || null,
      on_conflict: r.duplicate_of ? "merge" : "skip",
    })).filter((r) => r.email);
    if (rows.length === 0) {
      toast.error("Choose at least one contact with a valid email.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/contacts/import/commit", { contacts: rows });
      toast.success(`Added ${data.created}, merged ${data.merged}, skipped ${data.skipped}`);
      onImported?.();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-ink-900/50 p-2"
      onClick={onClose}
      data-testid="import-contacts-modal"
    >
      <div
        className="bg-white w-full max-w-2xl rounded-2xl sm:rounded-3xl shadow-card max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-ivory-200 flex items-center justify-between">
          <h2 className="font-heading text-xl font-semibold inline-flex items-center gap-2">
            <Users size={18} /> {stage === "chooser" ? "Import contacts" : `Review ${includedCount} contact${includedCount === 1 ? "" : "s"}`}
          </h2>
          <button onClick={onClose} className="btn-ghost !p-1.5" data-testid="import-close"><X size={18} /></button>
        </div>

        {stage === "chooser" && (
          <div className="p-5 space-y-3 overflow-y-auto">
            {ANDROID_PICKER_SUPPORTED() && (
              <button
                type="button"
                onClick={pickFromPhone}
                disabled={busy}
                className="w-full card-base p-4 hover:bg-ivory-100 text-left"
                data-testid="import-from-phone"
              >
                <div className="font-medium inline-flex items-center gap-2"><Smartphone size={16} /> Import from phone contacts</div>
                <div className="text-xs text-ink-500 mt-1">Open your Android contact picker. Choose one or more people — we'll only ask for name, email and phone.</div>
              </button>
            )}
            <label className="w-full card-base p-4 hover:bg-ivory-100 text-left block cursor-pointer" data-testid="import-from-file-label">
              <div className="font-medium inline-flex items-center gap-2"><FileUp size={16} /> Import contact file</div>
              <div className="text-xs text-ink-500 mt-1">Upload a .vcf (vCard) or .csv file up to 2 MB.</div>
              {IS_IPHONE() && (
                <div className="text-xs text-ink-600 mt-2 p-2 rounded-lg bg-ivory-100">
                  <strong>On iPhone:</strong> open Contacts, tap a person, choose <em>Share Contact</em>, save the vCard to Files, then select it here. You can share several by picking each one.
                </div>
              )}
              <input
                type="file"
                accept=".vcf,.csv,text/vcard,text/csv"
                className="hidden"
                onChange={onFileChosen}
                data-testid="import-file-input"
              />
            </label>
            <div className="p-3 text-xs text-ink-600 inline-flex items-start gap-2 rounded-lg bg-ivory-100">
              <ShieldCheck size={14} className="text-sage-600 mt-0.5 shrink-0" />
              <span>Only the contacts you select will be added. They will not be contacted automatically. We never keep the uploaded file after processing.</span>
            </div>
            {!ANDROID_PICKER_SUPPORTED() && !IS_IPHONE() && (
              <div className="p-3 text-xs text-ink-500 inline-flex items-start gap-2">
                <Info size={14} className="mt-0.5 shrink-0" />
                <span>Your browser can't open the phone address book directly. Export a vCard or CSV from your phone or contacts app and upload it here.</span>
              </div>
            )}
          </div>
        )}

        {stage === "review" && (
          <>
            <div className="p-4 border-b border-ivory-200 text-xs text-ink-600 inline-flex items-start gap-2 bg-ivory-100">
              <ShieldCheck size={14} className="text-sage-600 mt-0.5 shrink-0" />
              <span>Only the ticked contacts will be added. They won't be contacted automatically.</span>
            </div>
            <div className="flex-1 overflow-y-auto divide-y divide-ivory-200" data-testid="import-review-list">
              {proposed.map((row, i) => (
                <div key={i} className="p-4">
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={!!row.include}
                      onChange={() => toggleInclude(i)}
                      className="mt-1"
                      data-testid={`import-row-toggle-${i}`}
                    />
                    <div className="flex-1 min-w-0 space-y-2">
                      <input
                        type="text"
                        value={row.name}
                        onChange={(e) => updateRow(i, { name: e.target.value })}
                        placeholder="Full name"
                        className="input-field !py-1.5"
                        data-testid={`import-row-name-${i}`}
                      />
                      <div className="flex flex-wrap gap-2">
                        <select
                          value={row.email}
                          onChange={(e) => updateRow(i, { email: e.target.value })}
                          className="input-field !py-1.5 flex-1"
                          data-testid={`import-row-email-${i}`}
                        >
                          {(row.emails.length ? row.emails : [row.email || ""]).map((e) => (
                            <option key={e} value={e}>{e || "— no email —"}</option>
                          ))}
                        </select>
                        {row.phones.length > 0 && (
                          <select
                            value={row.phone}
                            onChange={(e) => updateRow(i, { phone: e.target.value })}
                            className="input-field !py-1.5 flex-1"
                            data-testid={`import-row-phone-${i}`}
                          >
                            {row.phones.map((p) => <option key={p} value={p}>{p}</option>)}
                          </select>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <input
                          type="text"
                          value={row.instrument || ""}
                          onChange={(e) => updateRow(i, { instrument: e.target.value })}
                          placeholder="Instrument (optional)"
                          className="input-field !py-1.5 flex-1"
                        />
                        <label className="inline-flex items-center gap-1 text-xs cursor-pointer">
                          <input type="checkbox" checked={!!row.is_favourite} onChange={(e) => updateRow(i, { is_favourite: e.target.checked })} />
                          Favourite
                        </label>
                      </div>
                      <input
                        type="text"
                        value={row.private_notes || ""}
                        onChange={(e) => updateRow(i, { private_notes: e.target.value })}
                        placeholder="Private notes (only visible to you)"
                        className="input-field !py-1.5"
                      />
                      <div className="flex flex-wrap gap-1">
                        {row.duplicate_of && (
                          <span className="pill bg-amber-soft text-amber-deep !text-[10px]" data-testid={`import-row-duplicate-${i}`}>
                            Already in your Contacts — will merge
                          </span>
                        )}
                        {row.is_amds_member && (
                          <span className="pill bg-sage-100 text-sage-700 !text-[10px] inline-flex items-center gap-1" data-testid={`import-row-member-${i}`}>
                            <UserCheck size={10} /> ✓ AMDS Member
                          </span>
                        )}
                        {!row.email && (
                          <span className="pill bg-ivory-200 text-ink-600 !text-[10px]">
                            No email — will be skipped
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="p-4 border-t border-ivory-200 flex items-center justify-between gap-3">
              <button type="button" className="btn-ghost text-sm" onClick={() => setStage("chooser")} data-testid="import-back">Back</button>
              <button
                type="button"
                disabled={busy || includedCount === 0}
                onClick={commit}
                className="btn-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="import-commit"
              >
                {busy ? "Importing…" : `Import selected contacts (${includedCount})`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}


function toReviewRow(c) {
  return {
    include: !!(c.emails && c.emails.length) || !!c.phones?.length,
    name: c.name || "",
    emails: c.emails || [],
    phones: c.phones || [],
    email: c.emails?.[0] || "",
    phone: c.phones?.[0] || "",
    instrument: "",
    is_favourite: false,
    private_notes: "",
    duplicate_of: c.duplicate_of || null,
    duplicate_name: c.duplicate_name || null,
    is_amds_member: !!c.is_amds_member,
  };
}
